import { Box, useTheme } from "@mui/material";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { useEffect } from "react";
import { configuratorActions } from "../../configurator-store";
import { FILTER_WIDTH } from "../../configurator-utils";
import { OptionsRail } from "./options-rail";
import { Filter } from "../filter";
import { SortOptions, ConfigurationsSortOptions } from "../sort-options";
import { GroupBy } from "../group-by";
import { ViewSettings } from "../view-settings";

const mapStateToProps = (state: RootState) => ({
  activeOptionsPanel: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.activeOptionsPanel,
  // needed to pick which Sort options component applies — Products and Configurations use different field sets
  activeTab: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.activeTab
});

const ownActions = {
  setActiveOptionsPanel: configuratorActions.setActiveOptionsPanel
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const OptionsPanelBase = (props: Props) => {
  const { activeOptionsPanel, activeTab, setActiveOptionsPanel } = props;
  const theme = useTheme();

  // Single shared Escape-closes-panel handler for whichever of the three is open
  useEffect(() => {
    if (!activeOptionsPanel) return;
    const handleEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setActiveOptionsPanel(null);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [activeOptionsPanel]);

  return (
    <>
      <OptionsRail />
      {/* Stays an in-flow flex sibling so opening it narrows the list instead of covering it. The
          width animation reflows the list every frame, which is why ChipList coalesces its
          ResizeObserver measurements to one per frame (see chip-list.tsx). */}
      <Box
        sx={{
          width: activeOptionsPanel ? FILTER_WIDTH.OPEN : 0,
          flexShrink: 0,
          transition: "width .35s ease",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
          backgroundColor: theme.palette.customColors.white,
          borderTop: `1px solid ${theme.palette.customColors.grey}`
        }}
      >
        {activeOptionsPanel === "filter" && <Filter />}
        {/* Sort is valid on both tabs now, but each tab sorts by a different field set, so the panel component itself differs */}
        {activeOptionsPanel === "sort" && (activeTab === "PRODUCTS" ? <SortOptions /> : <ConfigurationsSortOptions />)}
        {activeOptionsPanel === "group" && <GroupBy />}
        {activeOptionsPanel === "viewSettings" && <ViewSettings />}
      </Box>
    </>
  );
};

export const OptionsPanel = connect(mapStateToProps, ownActions)(OptionsPanelBase);
